import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";

export default function Tools() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/content/tools").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const [featured, ...rest] = items;

  return (
    <div data-testid="tools-page">
      <PageHero
        eyebrow="Ψηφιακά Εργαλεία"
        title="Η τεχνολογία στην πράξη."
        text="Λογισμικά και ψηφιακές εφαρμογές σχεδιασμένα για σύγχρονες ανάγκες εκπαίδευσης και παρέμβασης — με επιστημονική τεκμηρίωση στον πυρήνα τους."
      />

      <section className="pb-24 sm:pb-32 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {featured && (
            <Reveal>
              <div
                className="grid grid-cols-1 lg:grid-cols-2 bg-ink text-cream overflow-hidden"
                data-testid="tool-featured"
              >
                <div className="p-8 sm:p-12 flex flex-col justify-between min-h-[20rem]">
                  <div>
                    <div className="flex items-center gap-1.5 mb-8">
                      <span className="w-2.5 h-2.5 rounded-full bg-terracotta" />
                      <span className="w-2.5 h-2.5 rounded-full bg-ochre" />
                      <span className="w-2.5 h-2.5 rounded-full bg-sage" />
                      <span className="ml-3 font-mono2 text-[10px] tracking-wider text-cream/50">
                        noeticalab.app/{(featured.name || "").toLowerCase()}
                      </span>
                    </div>
                    <p className="eyebrow text-terracotta">Κύριο Εργαλείο</p>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold mt-4">{featured.name}</h2>
                    <p className="text-cream/70 leading-relaxed mt-4 max-w-md text-sm sm:text-base">{featured.description}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-8">
                    {(featured.tags || []).map((t) => (
                      <span key={t} className="font-mono2 text-[10px] tracking-[0.15em] uppercase border border-cream/25 px-2.5 py-1 text-cream/70">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                {featured.image && (
                  <div className="relative min-h-[16rem] lg:min-h-full">
                    <img src={featured.image} alt={featured.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-ink/20" />
                  </div>
                )}
              </div>
            </Reveal>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((t, i) => (
              <Reveal key={t.id} delay={(i % 3) * 0.08}>
                <div
                  className="group bg-sand border border-ink/10 h-full transition-transform duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_rgba(13,27,42,0.12)]"
                  data-testid={`tool-item-${i}`}
                >
                  <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-ink/10">
                    <span className="w-2.5 h-2.5 rounded-full bg-terracotta/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-ochre/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-sage/70" />
                    <span className="ml-3 font-mono2 text-[10px] tracking-wider text-slate2 truncate">
                      noeticalab.app/{(t.name || "").toLowerCase()}
                    </span>
                  </div>
                  {t.image && (
                    <div className="h-40 overflow-hidden">
                      <img
                        src={t.image}
                        alt={t.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-6 sm:p-7">
                    <h3 className="font-display text-xl sm:text-2xl font-semibold">{t.name}</h3>
                    <p className="text-slate2 text-sm leading-relaxed mt-3">{t.description}</p>
                    <div className="flex flex-wrap gap-2 mt-5">
                      {(t.tags || []).map((tag) => (
                        <span key={tag} className="font-mono2 text-[10px] tracking-[0.15em] uppercase border border-ink/15 px-2.5 py-1 text-ink/60">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <a
                      href="mailto:info@noeticalab.gr"
                      data-testid={`tool-cta-${i}`}
                      className="group/l inline-flex items-center gap-1.5 text-sm font-medium mt-6 text-terracottaD"
                    >
                      Δες το εργαλείο
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/l:translate-x-1" />
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {loaded && items.length === 0 && (
            <p className="text-slate2 font-mono2 text-sm tracking-wider" data-testid="tools-empty">
              Δεν υπάρχουν εργαλεία ακόμα.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
